/**
 * Policy Manager - Complete UI for Policy governance type
 * 
 * Manages guards, Cedar policies, decision models, and business rules
 * Part of the 15 fundamental workflow types system
 */

import { gql, useMutation, useQuery } from '@ferp/graphql-client';
import {
    ActionIcon,
    Alert,
    Badge,
    Button,
    Card,
    Group,
    JsonInput,
    Menu,
    Modal,
    NumberInput,
    ScrollArea,
    Select,
    Stack,
    Tabs,
    Text,
    TextInput,
    Textarea,
    Title
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
    IconAlertTriangle,
    IconBook,
    IconCheck,
    IconCode,
    IconCopy,
    IconDots,
    IconPencil,
    IconPlus,
    IconSettings,
    IconShield,
    IconTestPipe,
    IconTrash,
} from '@tabler/icons-react';
import React, { useCallback, useEffect, useState } from 'react';
import type { Policy, PolicyImplementation, PolicyRule } from '../types';
import { useGenerateId } from '../hooks/useGenerateId';
import { EntityType } from '../types/EntityType';

interface PolicyManagerProps {
  tenant: string;
  onPolicyChange?: (policies: Policy[]) => void;
  readonly?: boolean;
}

export const PolicyManager: React.FC<PolicyManagerProps> = ({
  tenant,
  onPolicyChange,
  readonly = false,
}) => {
  const [policies, setPolicies] = useState<Policy[]>([]);
  const [selectedPolicy, setSelectedPolicy] = useState<Policy | null>(null);
  const [editingPolicy, setEditingPolicy] = useState<Partial<Policy> | null>(null);
  const [loading, setLoading] = useState(false);
  const [testResult, setTestResult] = useState<any>(null);
  const [modalOpened, { open: openModal, close: closeModal }] = useDisclosure(false);

  // FERP 5-layer enforcement: Backend ID generation only
  const { generateId } = useGenerateId();

  // GraphQL wiring
  const GET_POLICIES = gql`
    query GetPolicies($tenant: String!) {
      policies(tenant: $tenant) {
        id
        name
        policyType
        scope
        rules { condition action priority }
        implementation { implType reference config }
        auditLevel
        metadata
      }
    }
  `;

  const UPSERT_POLICY = gql`
    mutation UpsertPolicy($tenant: String!, $policy: PolicyInput!) {
      upsertPolicy(tenant: $tenant, policy: $policy) { id }
    }
  `;

  const DELETE_POLICY = gql`
    mutation DeletePolicy($tenant: String!, $id: ID!) {
      deletePolicy(tenant: $tenant, id: $id)
    }
  `;

  const TEST_POLICY_MUT = gql`
    mutation TestPolicy($tenant: String!, $id: ID!) {
      testPolicy(tenant: $tenant, id: $id) { passed executionTimeMs violations }
    }
  `;

  const { data, loading: loadingPolicies, error: loadError, refetch } = useQuery<Record<string, any>>(GET_POLICIES, { variables: { tenant } } as any);
  const [mutateUpsert] = useMutation<Record<string, any>>(UPSERT_POLICY);
  const [mutateDelete] = useMutation<Record<string, any>>(DELETE_POLICY);
  const [mutateTest] = useMutation<Record<string, any>>(TEST_POLICY_MUT);

  useEffect(() => {
    if (data?.policies) {
      setPolicies(data.policies as Policy[]);
    }
  }, [data]);

  const createNewPolicy = useCallback(() => {
    const newPolicy: Partial<Policy> = {
      name: '',
      policyType: 'validation',  // Updated from 'business_rule' to 'validation' (Phase 1 migration)
      scope: 'workflow',  // Updated from 'tenant' to 'workflow' (Phase 1 migration)
      rules: [],
      implementation: {
        implType: 'cel',  // Updated from 'inline' to 'cel' (Phase 1 migration)
        config: {},
      },
      auditLevel: 'summary',
      metadata: {},
    };
    setEditingPolicy(newPolicy);
    setSelectedPolicy(null);
    openModal();
  }, [openModal]);

  const editPolicy = useCallback((policy: Policy) => {
    setEditingPolicy({ ...policy });
    setSelectedPolicy(policy);
    openModal();
  }, [openModal]);

  const savePolicy = useCallback(async () => {
    if (!editingPolicy || !editingPolicy.name) return;

    setLoading(true);
    try {
      // FERP architecture: Backend ID generation (5-layer enforcement)
      const policyId = editingPolicy.id || await generateId(EntityType.policy);

      const policyToSave: Policy = {
        id: policyId,
        name: editingPolicy.name,
        description: editingPolicy.description || '',
        policyType: editingPolicy.policyType || 'validation',  // Updated from 'business_rule' to 'validation'
        scope: editingPolicy.scope || 'workflow',  // Updated from 'tenant' to 'workflow'
        rules: editingPolicy.rules || [],
        implementation: editingPolicy.implementation || {
          implType: 'cel',  // Updated from 'inline' to 'cel'
          config: {},
        },
        auditLevel: editingPolicy.auditLevel || 'summary',
        active: editingPolicy.active ?? true,
        version: editingPolicy.version || 1,
        tags: editingPolicy.tags || [],
        metadata: editingPolicy.metadata || {},
        createdAt: editingPolicy.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        createdBy: editingPolicy.createdBy || 'current_user',
        updatedBy: 'current_user',
      };

      const updatedPolicies = selectedPolicy
        ? policies.map(p => p.id === selectedPolicy.id ? policyToSave : p)
        : [...policies, policyToSave];

      // Persist
      await mutateUpsert({ variables: { tenant, policy: policyToSave } });
      // Refresh from server to ensure canonical state
      await refetch?.();
      setPolicies(updatedPolicies);
      onPolicyChange?.(updatedPolicies);
      
      notifications.show({
        title: 'Success',
        message: `Policy "${policyToSave.name}" ${selectedPolicy ? 'updated' : 'created'} successfully`,
        color: 'green',
        icon: <IconCheck size={16} />,
      });
      
      closeModal();
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to save policy',
        color: 'red',
        icon: <IconAlertTriangle size={16} />,
      });
    } finally {
      setLoading(false);
    }
  }, [editingPolicy, selectedPolicy, policies, onPolicyChange, closeModal]);

  const deletePolicy = useCallback(async (policyId: string) => {
    try {
      setLoading(true);
      await mutateDelete({ variables: { tenant, id: policyId } });
      await refetch?.();
      const updatedPolicies = policies.filter(p => p.id !== policyId);
      setPolicies(updatedPolicies);
      onPolicyChange?.(updatedPolicies);
      notifications.show({ title: 'Policy Deleted', message: 'Policy has been removed', color: 'orange' });
    } finally {
      setLoading(false);
    }
  }, [policies, onPolicyChange, mutateDelete, tenant, refetch]);

  const testPolicy = useCallback(async (policy: Policy) => {
    setLoading(true);
    try {
      const res = await mutateTest({ variables: { tenant, id: policy.id } });
      const tr = res?.data?.testPolicy || null;
      setTestResult(tr);
      if (tr) {
        notifications.show({
          title: 'Policy Test Complete',
          message: `Test ${tr.passed ? 'passed' : 'failed'} in ${tr.executionTimeMs}ms`,
          color: tr.passed ? 'green' : 'red',
          icon: tr.passed ? <IconCheck size={16} /> : <IconAlertTriangle size={16} />,
        });
      }
    } finally {
      setLoading(false);
    }
  }, [mutateTest, tenant]);

  const getPolicyTypeColor = (type: Policy['policyType']) => {
    // UPDATED: New PolicyType enum values (Phase 1 migration complete)
    const colors: Record<string, string> = {
      // NEW types
      approval: 'indigo',
      security: 'red',
      notification: 'yellow',

      // Existing types
      validation: 'teal',
      compliance: 'purple',

      // DEPRECATED types (backward compatibility)
      guard: 'blue',
      authorization: 'green',
      business_rule: 'orange',
      governance: 'grape',
    };
    return colors[type] || 'gray';
  };

  const getScopeIcon = (scope: Policy['scope']) => {
    const icons: Record<string, string> = {
      workflow: '⚙️',
      task: '📋',
      approval: '✅',
      global: '🌐',

      // DEPRECATED scopes (backward compatibility)
      tenant: '🏢',
      field: '📄',
      entity: '🗃️',
    };
    return icons[scope] || '📄';
  };

  return (
    <Stack gap="md">
      <Group justify="apart">
        <div>
          <Title order={3}>
            <Group gap="xs">
              <IconShield size={24} />
              Policy Management
            </Group>
          </Title>
          <Text size="sm" c="dimmed">
            Manage approval routing, security policies, validation rules, and compliance requirements ({policies.length} policies)
          </Text>
        </div>
        {!readonly && (
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={createNewPolicy}
            variant="filled"
          >
            New Policy
          </Button>
        )}
      </Group>

      {policies.length === 0 ? (
        <Alert icon={<IconBook size={16} />} title="No Policies Configured">
          <Text size="sm">
            No policies have been configured yet. Click &quot;New Policy&quot; to create your first approval, security, validation, or compliance policy.
          </Text>
        </Alert>
      ) : (
        <Stack gap="sm">
          {policies.map((policy) => (
            <Card key={policy.id} shadow="sm" padding="md" withBorder>
              <Group justify="apart" ta="left">
                <div style={{ flex: 1 }}>
                  <Group gap="xs" ta="center">
                    <Text fw={600}>{policy.name}</Text>
                    <Text size="xs">{getScopeIcon(policy.scope)}</Text>
                    <Badge
                      color={getPolicyTypeColor(policy.policyType)}
                      variant="light"
                      size="sm"
                    >
                      {policy.policyType.replace('_', ' ').toUpperCase()}
                    </Badge>
                    <Badge
                      color={policy.auditLevel === 'full' ? 'red' : policy.auditLevel === 'summary' ? 'orange' : 'gray'}
                      variant="outline"
                      size="xs"
                    >
                      {policy.auditLevel}
                    </Badge>
                  </Group>
                  
                  <Text size="sm" color="dimmed" mt="xs">
                    {policy.rules.length} rule{policy.rules.length !== 1 ? 's' : ''} • {policy.implementation.implType} implementation
                  </Text>
                  
                  {policy.metadata?.complianceRequirement && (
                    <Badge color="purple" variant="outline" size="xs" mt="xs">
                      {policy.metadata.complianceRequirement}
                    </Badge>
                  )}
                </div>
                
                {!readonly && (
                  <Menu shadow="md" width={200}>
                    <Menu.Target>
                      <ActionIcon variant="subtle">
                        <IconDots size={16} />
                      </ActionIcon>
                    </Menu.Target>
                    
                    <Menu.Dropdown>
                      <Menu.Item 
                        leftSection={<IconPencil size={14} />}
                        onClick={() => editPolicy(policy)}
                      >
                        Edit Policy
                      </Menu.Item>
                      <Menu.Item 
                        leftSection={<IconTestPipe size={14} />}
                        onClick={() => testPolicy(policy)}
                        disabled={loading}
                      >
                        Test Policy
                      </Menu.Item>
                      <Menu.Item
                        leftSection={<IconCopy size={14} />}
                        onClick={async () => {
                          // FERP architecture: Backend ID generation (5-layer enforcement)
                          const duplicateId = await generateId(EntityType.policy);
                          const duplicate = { ...policy, id: duplicateId, name: `${policy.name} (Copy)` };
                          setPolicies([...policies, duplicate]);
                        }}
                      >
                        Duplicate
                      </Menu.Item>
                      <Menu.Divider />
                      <Menu.Item 
                        leftSection={<IconTrash size={14} />}
                        color="red"
                        onClick={() => deletePolicy(policy.id)}
                      >
                        Delete
                      </Menu.Item>
                    </Menu.Dropdown>
                  </Menu>
                )}
              </Group>
              
              {testResult && (
                <Alert 
                  mt="sm"
                  color={testResult.passed ? 'green' : 'red'}
                  icon={testResult.passed ? <IconCheck size={16} /> : <IconAlertTriangle size={16} />}
                >
                  <Text size="sm">
                    Last test: {testResult.passed ? 'Passed' : 'Failed'} in {testResult.executionTimeMs}ms
                    {testResult.violations?.length > 0 && (
                      <Text size="xs" mt="xs">Violations: {testResult.violations.join(', ')}</Text>
                    )}
                  </Text>
                </Alert>
              )}
            </Card>
          ))}
        </Stack>
      )}

      <Modal
        opened={modalOpened}
        onClose={closeModal}
        title={selectedPolicy ? 'Edit Policy' : 'Create New Policy'}
        size="lg"
        overlayProps={{ opacity: 0.55, blur: 3 }}
      >
        {editingPolicy && (
          <Tabs defaultValue="basic" variant="outline">
            <Tabs.List>
              <Tabs.Tab value="basic" leftSection={<IconSettings size={16} />}>
                Basic Info
              </Tabs.Tab>
              <Tabs.Tab value="rules" leftSection={<IconCode size={16} />}>
                Rules ({editingPolicy.rules?.length || 0})
              </Tabs.Tab>
              <Tabs.Tab value="implementation" leftSection={<IconSettings size={16} />}>
                Implementation
              </Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="basic" pt="md">
              <Stack gap="md">
                <TextInput
                  label="Policy Name"
                  placeholder="Enter policy name"
                  required
                  value={editingPolicy.name || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditingPolicy({ ...editingPolicy, name: e.target.value })}
                />
                
                <Group grow>
                  <Select
                    label="Policy Type"
                    placeholder="Select type"
                    required
                    value={editingPolicy.policyType}
                    onChange={(value: string | null) => setEditingPolicy({
                      ...editingPolicy,
                      policyType: value as Policy['policyType']
                    })}
                    data={[
                      // NEW types (Phase 1 migration complete)
                      { value: 'approval', label: 'Approval - Approval routing & dual control (NEW)' },
                      { value: 'security', label: 'Security - Access control & authorization (REPLACES Guard/Authorization)' },
                      { value: 'validation', label: 'Validation - Data validation & business rules' },
                      { value: 'compliance', label: 'Compliance - Regulatory compliance' },
                      { value: 'notification', label: 'Notification - Communication routing (NEW)' },
                    ]}
                  />

                  <Select
                    label="Scope"
                    placeholder="Select scope"
                    required
                    value={editingPolicy.scope}
                    onChange={(value: string | null) => setEditingPolicy({
                      ...editingPolicy,
                      scope: value as Policy['scope']
                    })}
                    data={[
                      // NEW scopes (Phase 1 migration complete)
                      { value: 'workflow', label: 'Workflow - Entire workflow lifecycle' },
                      { value: 'task', label: 'Task - Task-specific policies (NEW)' },
                      { value: 'approval', label: 'Approval - Approval gates (NEW - CRITICAL for dual control)' },
                      { value: 'global', label: 'Global - Tenant-wide (all workflows)' },
                    ]}
                  />
                </Group>
                
                <Select
                  label="Audit Level"
                  placeholder="Select audit level"
                  value={editingPolicy.auditLevel}
                  onChange={(value: string | null) => setEditingPolicy({
                    ...editingPolicy,
                    auditLevel: value as Policy['auditLevel']
                  })}
                  data={[
                    { value: 'none', label: 'None' },
                    { value: 'summary', label: 'Summary' },
                    { value: 'full', label: 'Full' },
                  ]}
                />
                
                <JsonInput
                  label="Metadata"
                  placeholder="Enter metadata JSON"
                  value={JSON.stringify(editingPolicy.metadata || {}, null, 2)}
                  onChange={(value) => {
                    try {
                      setEditingPolicy({ ...editingPolicy, metadata: JSON.parse(value) });
                    } catch (e) {
                      // Invalid JSON, ignore
                    }
                  }}
                  minRows={3}
                />
              </Stack>
            </Tabs.Panel>

            <Tabs.Panel value="rules" pt="md">
              <ScrollArea h={300}>
                <Stack gap="sm">
                  {editingPolicy.rules?.map((rule, index) => (
                    <Card key={index} withBorder padding="sm">
                      <Group justify="apart" ta="left">
                        <div style={{ flex: 1 }}>
                          <Textarea
                            label="Condition"
                            placeholder="Enter condition logic"
                            value={rule.condition}
                            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                              const newRules = [...(editingPolicy.rules || [])];
                              newRules[index] = { ...rule, condition: e.target.value };
                              setEditingPolicy({ ...editingPolicy, rules: newRules });
                            }}
                            size="xs"
                            autosize
                            minRows={2}
                          />
                          
                          <Group mt="xs">
                            <TextInput
                              label="Action"
                              placeholder="allow/deny/block"
                              value={rule.action}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                const newRules = [...(editingPolicy.rules || [])];
                                newRules[index] = { ...rule, action: e.target.value };
                                setEditingPolicy({ ...editingPolicy, rules: newRules });
                              }}
                              size="xs"
                            />
                            
                            <NumberInput
                              label="Priority"
                              value={rule.priority}
                              onChange={(value: string | number) => {
                                const newRules = [...(editingPolicy.rules || [])];
                                newRules[index] = { ...rule, priority: Number(value) || 0 };
                                setEditingPolicy({ ...editingPolicy, rules: newRules });
                              }}
                              size="xs"
                              min={0}
                              max={999}
                            />
                          </Group>
                        </div>
                        
                        <ActionIcon
                          color="red"
                          variant="subtle"
                          onClick={() => {
                            const newRules = editingPolicy.rules?.filter((_: any, i) => i !== index) || [];
                            setEditingPolicy({ ...editingPolicy, rules: newRules });
                          }}
                        >
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Group>
                    </Card>
                  ))}
                  
                  <Button
                    variant="light"
                    leftSection={<IconPlus size={16} />}
                    onClick={() => {
                      const newRule: PolicyRule = {
                        condition: '',
                        action: 'allow',
                        priority: 100,
                        parameters: {},
                      };
                      setEditingPolicy({
                        ...editingPolicy,
                        rules: [...(editingPolicy.rules || []), newRule],
                      });
                    }}
                  >
                    Add Rule
                  </Button>
                </Stack>
              </ScrollArea>
            </Tabs.Panel>

            <Tabs.Panel value="implementation" pt="md">
              <Stack gap="md">
                <Select
                  label="Implementation Type"
                  placeholder="Select implementation type"
                  value={editingPolicy.implementation?.implType}
                  onChange={(value: string | null) => setEditingPolicy({
                    ...editingPolicy,
                    implementation: {
                      ...editingPolicy.implementation!,
                      implType: value as PolicyImplementation['implType']
                    }
                  })}
                  data={[
                    // NEW types (Phase 1 migration complete)
                    { value: 'zen', label: 'Zen - Zen Engine (<1ms, Redis cached)' },
                    { value: 'cel', label: 'CEL - Common Expression Language (Google/K8s standard)' },
                    { value: 'json_logic', label: 'JSON Logic - Simple rule engine' },
                    { value: 'cedar', label: 'Cedar - AWS Cedar (formally verified)' },
                  ]}
                />
                
                <TextInput
                  label="Reference"
                  placeholder="Policy reference or identifier"
                  value={editingPolicy.implementation?.reference || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditingPolicy({
                    ...editingPolicy,
                    implementation: {
                      ...editingPolicy.implementation!,
                      reference: e.target.value
                    }
                  })}
                />
                
                <JsonInput
                  label="Implementation Config"
                  placeholder="Enter implementation configuration"
                  value={JSON.stringify(editingPolicy.implementation?.config || {}, null, 2)}
                  onChange={(value) => {
                    try {
                      setEditingPolicy({
                        ...editingPolicy,
                        implementation: {
                          ...editingPolicy.implementation!,
                          config: JSON.parse(value)
                        }
                      });
                    } catch (e) {
                      // Invalid JSON, ignore
                    }
                  }}
                  minRows={4}
                />
              </Stack>
            </Tabs.Panel>
          </Tabs>
        )}
        
        <Group justify="apart" mt="md">
          <Button variant="light" onClick={closeModal}>
            Cancel
          </Button>
          
          <Button
            onClick={savePolicy}
            loading={loading}
            leftSection={<IconCheck size={16} />}
          >
            {selectedPolicy ? 'Update' : 'Create'} Policy
          </Button>
        </Group>
      </Modal>
    </Stack>
  );
};

export default PolicyManager;